import type { DemoBooking } from './api';

/**
 * Consumer email domains: an address on one says nothing about the person's
 * company. Mirrors packages/api/src/modules/project-management/webmail-domains.ts,
 * so the page drops a webmail company match even from an API that still sends
 * one (onejamesk@outlook.com was shown as "Microsoft", review #10).
 */
const WEBMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com', 'googlemail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'msn.com',
  'protonmail.com', 'proton.me', 'pm.me', 'tutanota.com', 'tuta.io', 'fastmail.com', 'hey.com', 'zoho.com',
  'mail.com', 'email.com', 'yandex.com', 'yandex.ru', 'mail.ru', 'qq.com', '163.com', '126.com',
  'web.de', 't-online.de', 'freenet.de', 'arcor.de',
  'orange.fr', 'free.fr', 'wanadoo.fr', 'laposte.net', 'sfr.fr', 'neuf.fr', 'bbox.fr',
  'telenet.be', 'skynet.be', 'proximus.be', 'scarlet.be',
  'ziggo.nl', 'kpnmail.nl', 'hetnet.nl', 'planet.nl', 'home.nl', 'xs4all.nl', 'casema.nl',
  'bluewin.ch', 'btinternet.com', 'sky.com', 'virginmedia.com', 'talktalk.net', 'ntlworld.com',
  'libero.it', 'virgilio.it', 'tiscali.it', 'alice.it',
]);

const WEBMAIL_BRAND = /^(outlook|hotmail|live|windowslive|yahoo|ymail|rocketmail|gmx)\.[a-z]{2,3}(\.[a-z]{2})?$/;

/** The domain of an address, lower-case; empty when there is none. */
export function emailDomain(email: string | null | undefined): string {
  const raw = (email ?? '').trim().toLowerCase();
  return raw.includes('@') ? raw.slice(raw.lastIndexOf('@') + 1) : '';
}

/** True for an address on a consumer (webmail or ISP) domain. */
export function isWebmailAddress(email: string | null | undefined): boolean {
  const domain = emailDomain(email);
  return !!domain && (WEBMAIL_DOMAINS.has(domain) || WEBMAIL_BRAND.test(domain));
}


/**
 * The company to show: never one read off a webmail address (Attio links
 * outlook.com to Microsoft, hotmail.com to "Hotmail"), and never the webmail
 * domain itself as a company. A company the invitee typed in the form stays.
 */
export function bookingCompany(b: Pick<DemoBooking, 'inviteeEmail' | 'inviteeCompany' | 'attioCompanyName'>): {
  attioName: string | null;
  typed: string | null;
  webmail: boolean;
} {
  const webmail = isWebmailAddress(b.inviteeEmail);
  if (!webmail) return { attioName: b.attioCompanyName ?? null, typed: b.inviteeCompany, webmail };
  const typed = b.inviteeCompany && b.inviteeCompany.toLowerCase() !== emailDomain(b.inviteeEmail) ? b.inviteeCompany : null;
  return { attioName: null, typed, webmail };
}
